import React, { useMemo } from "react";
import {
  Group,
  Text,
  Divider,
  Badge,
  useMantineTheme,
  Select,
} from "@mantine/core";
import { AppButton, AppToolbar } from "@/components/generic";
import { VenueSelector } from "@/components/features";
import { useVenuesContext } from "@/contexts";

export interface ImportToolbarProps {
  /** Total number of events available */
  totalEvents: number;
  /** Number of events currently selected */
  selectedCount: number;
  /** Number of events currently being imported */
  importingCount: number;
  /** Whether any bulk operation is in progress */
  bulkImporting?: boolean;
  /** Whether the toolbar is disabled */
  disabled?: boolean;
  /** Whether selection controls should be shown */
  showSelectionControls?: boolean;
  /** Current import mode */
  importMode?: "merge" | "replace";
  /** Whether venue and mode selectors should be shown */
  showVenueAndModeSelectors?: boolean;
  /** Callback to select all events */
  onSelectAll?: () => void;
  /** Callback to deselect all events */
  onDeselectAll?: () => void;
  /** Callback to import selected events */
  onImportSelected?: (venueId: string, importMode: "merge" | "replace") => void;
  /** Callback to import all events */
  onImportAll?: (venueId: string, importMode: "merge" | "replace") => void;
  /** Callback to cancel current import operation */
  onCancelImport?: () => void;
  /** Callback when import mode changes */
  onImportModeChange?: (mode: "merge" | "replace") => void;
  /** Custom actions to add to the toolbar */
  customActions?: React.ReactNode[];
}

export const ImportToolbar: React.FC<ImportToolbarProps> = ({
  totalEvents,
  selectedCount,
  importingCount,
  bulkImporting = false,
  disabled = false,
  showSelectionControls = true,
  importMode = "merge",
  showVenueAndModeSelectors = true,
  onSelectAll,
  onDeselectAll,
  onImportSelected,
  onImportAll,
  onCancelImport,
  onImportModeChange,
  customActions = [],
}) => {
  const theme = useMantineTheme();
  const { selectedVenue, setSelectedVenue } = useVenuesContext();

  // Calculate selection statistics
  const stats = useMemo(() => {
    const allSelected = selectedCount === totalEvents && totalEvents > 0;
    const noneSelected = selectedCount === 0;
    const partialSelected = selectedCount > 0 && selectedCount < totalEvents;
    const hasEvents = totalEvents > 0;
    const hasVenue = Boolean(selectedVenue?.id);
    const canImportSelected = selectedCount > 0 && !bulkImporting && hasVenue;
    const canImportAll = totalEvents > 0 && !bulkImporting && hasVenue;

    return {
      allSelected,
      noneSelected,
      partialSelected,
      hasEvents,
      hasVenue,
      canImportSelected,
      canImportAll,
    };
  }, [selectedCount, totalEvents, bulkImporting, selectedVenue?.id]);

  // Handle venue selection change
  const handleVenueChange = (venueId: string | null) => {
    if (venueId && selectedVenue?.id !== venueId) {
      // Find and set the venue object - this is a simplified approach
      // In a real app, you might want to fetch the venue object by ID
      // For now, we'll just update with the ID and let the context handle it
      setSelectedVenue({ id: venueId } as any);
    } else if (!venueId) {
      setSelectedVenue(null);
    }
  };

  // Handle import mode change
  const handleImportModeChange = (value: string | null) => {
    if (value && (value === "merge" || value === "replace")) {
      onImportModeChange?.(value);
    }
  };

  // Handle import selected
  const handleImportSelected = () => {
    if (selectedVenue?.id && onImportSelected) {
      onImportSelected(selectedVenue.id, importMode);
    }
  };

  // Handle import all
  const handleImportAll = () => {
    if (selectedVenue?.id && onImportAll) {
      onImportAll(selectedVenue.id, importMode);
    }
  };

  // Get selection control button text and state
  const getSelectionControlProps = () => {
    if (stats.allSelected) {
      return {
        text: "Deselect All",
        action: onDeselectAll,
        iconName: "Square" as const,
      };
    } else {
      return {
        text: "Select All",
        action: onSelectAll,
        iconName: "SquareCheck" as const,
      };
    }
  };

  const selectionControl = getSelectionControlProps();

  // Progress indicator text
  const getProgressText = (): string => {
    if (bulkImporting && importingCount > 0) {
      return `Importing ${importingCount} of ${
        selectedCount > 0 ? selectedCount : totalEvents
      } events...`;
    }
    if (selectedCount > 0) {
      return `${selectedCount} of ${totalEvents} events selected`;
    }
    if (totalEvents > 0) {
      return `${totalEvents} events available`;
    }
    return "No events to import";
  };

  // Import mode options
  const importModeOptions = [
    { value: "merge", label: "Merge (Update existing)" },
    { value: "replace", label: "Replace (Overwrite all)" },
  ];

  return (
    <AppToolbar
      sections={[
        // Left side - Venue selector, mode selector, and selection info
        {
          id: "venue-and-selection",
          items: [
            // Venue selector
            ...(showVenueAndModeSelectors
              ? [
                  <VenueSelector
                    key="venue-selector"
                    value={selectedVenue?.id || null}
                    onChange={handleVenueChange}
                    placeholder="Select venue for import"
                    style={{ minWidth: 200 }}
                    required
                    clearable={false}
                    disabled={disabled || bulkImporting}
                  />,
                  <Select
                    key="import-mode"
                    value={importMode}
                    onChange={handleImportModeChange}
                    data={importModeOptions}
                    size="md"
                    style={{ minWidth: 180 }}
                    disabled={disabled || bulkImporting}
                    allowDeselect={false}
                  />,
                  <Divider key="divider-venue" orientation="vertical" />,
                ]
              : []),

            // Selection count and status
            <Group key="selection-status" gap="sm">
              <Text size="sm" fw={500}>
                {getProgressText()}
              </Text>

              {bulkImporting && (
                <Badge
                  size="sm"
                  color="blue"
                  variant="light"
                  leftSection={
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: theme.colors.blue[6],
                        animation: "pulse 1.5s ease-in-out infinite",
                      }}
                    />
                  }
                >
                  Importing
                </Badge>
              )}

              {stats.partialSelected && !bulkImporting && (
                <Badge size="sm" color="orange" variant="light">
                  Partial Selection
                </Badge>
              )}

              {!stats.hasVenue && stats.hasEvents && !bulkImporting && (
                <Badge size="sm" color="red" variant="light">
                  No Venue Selected
                </Badge>
              )}
            </Group>,

            // Selection controls
            ...(showSelectionControls && stats.hasEvents
              ? [
                  <Divider key="divider-selection" orientation="vertical" />,
                  <AppButton
                    key="selection-control"
                    variant="secondary"
                    size="sm"
                    iconName={selectionControl.iconName}
                    onClick={selectionControl.action}
                    disabled={disabled || bulkImporting}
                  >
                    {selectionControl.text}
                  </AppButton>,
                ]
              : []),
          ],
          align: "left",
          gap: "sm",
        },

        // Right side - Import actions
        {
          id: "import-actions",
          items: [
            // Custom actions
            ...customActions,

            // Import selected button
            ...(stats.canImportSelected
              ? [
                  <AppButton
                    key="import-selected"
                    iconName="TransferIn"
                    onClick={handleImportSelected}
                    disabled={disabled || !stats.canImportSelected}
                    loading={bulkImporting}
                  >
                    Import Selected ({selectedCount})
                  </AppButton>,
                ]
              : []),

            // Import all button
            ...(stats.canImportAll
              ? [
                  <AppButton
                    key="import-all"
                    variant="secondary"
                    iconName="TransferIn"
                    onClick={handleImportAll}
                    disabled={disabled || !stats.canImportAll}
                    loading={bulkImporting}
                  >
                    Import All ({totalEvents})
                  </AppButton>,
                ]
              : []),

            // Cancel import button (shown during bulk import)
            ...(bulkImporting && onCancelImport
              ? [
                  <Divider key="divider-cancel" orientation="vertical" />,
                  <AppButton
                    key="cancel-import"
                    variant="danger"
                    iconName="X"
                    onClick={onCancelImport}
                    disabled={disabled}
                  >
                    Cancel
                  </AppButton>,
                ]
              : []),
          ],
          align: "right",
          gap: "sm",
        },
      ]}
      style={{
        backgroundColor: bulkImporting
          ? theme.colors.blue[0]
          : theme.colors.gray[0],
        borderColor: bulkImporting
          ? theme.colors.blue[3]
          : theme.colors.gray[3],
        transition: "all 200ms ease",
      }}
    />
  );
};
